import React, { useState, useEffect } from 'react'
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

const MONTH_NAMES = [
  'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
  'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'
]

const MONTH_NAMES_FULL = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
]

export default function PeriodSelectorDialog({ 
  open, 
  onClose, 
  availablePeriods, 
  currentPeriod,
  onApply 
}) {
  const theme = useTheme()
  const [mode, setMode] = useState('MONTH') // 'YEAR' or 'MONTH'
  const [selectedYear, setSelectedYear] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState(null)

  // Debug logging
  useEffect(() => {
    if (open) {
      console.log('PeriodSelectorDialog opened')
      console.log('availablePeriods:', availablePeriods)
      console.log('currentPeriod:', currentPeriod)
    }
  }, [open, availablePeriods, currentPeriod])

  // Initialize from current period
  useEffect(() => {
    if (currentPeriod) {
      const currentDate = new Date(currentPeriod.period_start)
      setMode(currentPeriod.period_type || 'MONTH')
      setSelectedYear(currentDate.getFullYear())
      setSelectedMonth(currentPeriod.period_type === 'MONTH' ? currentDate.getMonth() + 1 : null)
    } else if (availablePeriods.length > 0) {
      const latest = availablePeriods[0]
      const latestDate = new Date(latest.period_start)
      setMode(latest.period_type || 'MONTH')
      setSelectedYear(latestDate.getFullYear())
      setSelectedMonth(latest.period_type === 'MONTH' ? latestDate.getMonth() + 1 : null)
    }
  }, [currentPeriod, availablePeriods, open])

  // Extract year and month from period_start for easier processing
  const processedPeriods = availablePeriods.map(period => {
    const startDate = new Date(period.period_start)
    return {
      ...period,
      year: startDate.getFullYear(),
      month: period.period_type === 'MONTH' ? startDate.getMonth() + 1 : null
    }
  })

  // Get unique years from available periods (with safety checks)
  const availableYears = [...new Set(
    processedPeriods
      .filter(p => p && p.year) // Filter out null/undefined periods and years
      .map(p => p.year)
  )].sort((a, b) => b - a)

  // Get available months for selected year
  const availableMonthsForYear = processedPeriods
    .filter(p => p && p.year === selectedYear && p.period_type === 'MONTH')
    .map(p => p.month)
    .filter(month => month) // Filter out null/undefined months

  // Debug logging for availability
  useEffect(() => {
    if (open) {
      console.log('=== PeriodSelectorDialog Debug ===')
      console.log('Mode:', mode)
      console.log('Selected Year:', selectedYear)
      console.log('Raw availablePeriods:', availablePeriods)
      console.log('Processed periods:', processedPeriods)
      console.log('Filtered Available Years:', availableYears)
      console.log('Available Months for Year:', availableMonthsForYear)
      console.log('================================')
    }
  }, [open, mode, selectedYear, availableYears, availableMonthsForYear, processedPeriods])

  // Check if a year has any snapshots (for the current mode)
  const isYearAvailable = (year) => {
    if (mode === 'YEAR') {
      // In YEAR mode, check for YEAR snapshots
      return processedPeriods.some(p => p.year === year && p.period_type === 'YEAR')
    } else {
      // In MONTH mode, check if year has any MONTH snapshots
      return processedPeriods.some(p => p.year === year && p.period_type === 'MONTH')
    }
  }

  // Check if a month has any snapshots
  const isMonthAvailable = (month) => {
    return availableMonthsForYear.includes(month)
  }

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode)
      if (newMode === 'YEAR') {
        setSelectedMonth(null)
      }
    }
  }

  const handleYearClick = (year) => {
    setSelectedYear(year)
    if (mode === 'MONTH') {
      // Auto-select first available month for this year
      const firstMonth = processedPeriods
        .filter(p => p.year === year && p.period_type === 'MONTH')
        .sort((a, b) => (b.month || 0) - (a.month || 0))[0]
      if (firstMonth) {
        setSelectedMonth(firstMonth.month)
      }
    }
  }

  const handleMonthClick = (month) => {
    if (isMonthAvailable(month)) {
      setSelectedMonth(month)
    }
  }

  const handleApply = () => {
    // Find the matching period from processed periods
    const matchingPeriod = processedPeriods.find(p => {
      if (mode === 'YEAR') {
        return p.year === selectedYear && p.period_type === 'YEAR'
      } else {
        return p.year === selectedYear && p.month === selectedMonth && p.period_type === 'MONTH'
      }
    })

    if (matchingPeriod) {
      // Return the original period format with period_start, period_end, etc.
      onApply(matchingPeriod)
    } else {
      // Fallback: construct period object
      const period = {
        period_type: mode,
        year: selectedYear,
        month: mode === 'MONTH' ? selectedMonth : null
      }
      onApply(period)
    }
    onClose()
  }

  const isApplyDisabled = () => {
    if (!selectedYear) return true
    if (mode === 'YEAR') {
      return !isYearAvailable(selectedYear)
    }
    if (mode === 'MONTH') {
      return !selectedMonth || !isMonthAvailable(selectedMonth)
    }
    return false
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.appBar?.main || '#0a1929',
          color: 'white',
          borderRadius: 3,
          p: 0,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2.5,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Sélectionner une période
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.08)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {/* Mode Toggle */}
        <Box display="flex" justifyContent="center" mb={3}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            sx={{
              bgcolor: 'rgba(255,255,255,0.05)',
              borderRadius: 2,
              '& .MuiToggleButton-root': {
                color: 'rgba(255,255,255,0.7)',
                border: 'none',
                px: 4,
                py: 1,
                textTransform: 'none',
                fontSize: 15,
                fontWeight: 500,
                '&.Mui-selected': {
                  bgcolor: '#0089ff',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#0070cc',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
              },
            }}
          >
            <ToggleButton value="YEAR">Année</ToggleButton>
            <ToggleButton value="MONTH">Mois</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Year Selection */}
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            sx={{ color: 'rgba(255,255,255,0.6)', mb: 1.5, fontSize: 13 }}
          >
            Sélectionner l'année
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 1,
            }}
          >
            {availableYears.map((year) => {
              const isSelected = year === selectedYear
              const isAvailable = isYearAvailable(year)
              
              return (
                <Button
                  key={year}
                  onClick={() => handleYearClick(year)}
                  disabled={!isAvailable}
                  sx={{
                    py: 1.5,
                    bgcolor: isSelected ? '#0089ff' : 'rgba(255,255,255,0.05)',
                    color: isSelected ? 'white' : isAvailable ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.3)',
                    border: isSelected ? '2px solid #0089ff' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: isSelected ? 600 : 400,
                    '&:hover': {
                      bgcolor: isSelected ? '#0070cc' : 'rgba(255,255,255,0.08)',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.2)',
                      bgcolor: 'rgba(255,255,255,0.02)',
                    },
                  }}
                >
                  {year || 'N/A'}
                </Button>
              )
            })}
          </Box>
        </Box>

        {/* Month Selection (only in MONTH mode) */}
        {mode === 'MONTH' && (
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: 'rgba(255,255,255,0.6)', mb: 1.5, fontSize: 13 }}
            >
              Sélectionner le mois
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1,
              }}
            >
              {MONTH_NAMES.map((monthName, index) => {
                const monthNumber = index + 1
                const isSelected = monthNumber === selectedMonth
                const isAvailable = isMonthAvailable(monthNumber)
                
                return (
                  <Button
                    key={monthNumber}
                    onClick={() => handleMonthClick(monthNumber)}
                    disabled={!isAvailable}
                    sx={{
                      py: 1.5,
                      bgcolor: isSelected ? '#0089ff' : 'rgba(255,255,255,0.05)',
                      color: isSelected ? 'white' : isAvailable ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.3)',
                      border: isSelected ? '2px solid #0089ff' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: 14,
                      fontWeight: isSelected ? 600 : 400,
                      '&:hover': {
                        bgcolor: isSelected ? '#0070cc' : 'rgba(255,255,255,0.08)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255,255,255,0.2)',
                        bgcolor: 'rgba(255,255,255,0.02)',
                      },
                    }}
                  >
                    {monthName}
                  </Button>
                )
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 3,
          pt: 2,
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={isApplyDisabled()}
          sx={{
            bgcolor: '#0089ff',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            px: 6,
            py: 1.5,
            fontSize: 15,
            borderRadius: 2,
            '&:hover': {
              bgcolor: '#0070cc',
            },
            '&.Mui-disabled': {
              bgcolor: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.3)',
            },
          }}
        >
          Appliquer
        </Button>
      </Box>
    </Dialog>
  )
}
